import {
  Component,
  inject,
  output,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Button } from "../utils/button/button";
import { Loader } from "../loader/loader";
import { TranslocoModule } from "@jsverse/transloco";
import { SearchService } from "src/app/features/services/search/search-service";
import {
  SearchTileInterface,
  TileInterface,
  VideoTileDto,
} from "src/app/core/interfaces/tile-interface";
import { ContactCardInterface } from "src/app/core/interfaces/contact-card-interface";
import { MobilityContactCardInterface } from "src/app/core/interfaces/mobility-contact-card-interface";
import { TilesApiService } from "src/app/core/services/tiles/tiles-service";
import { Tile } from "../tile/tile";
import { Icon } from "../utils/icon/icon";
import { CardResult } from "../card-result/card-result";
import { ContactCard } from "../contact-card/contact-card";
import { Router } from "@angular/router";

@Component({
  selector: "search-bar",
  imports: [
    Button,
    Loader,
    TranslocoModule,
    Tile,
    Icon,
    CardResult,
    ContactCard,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./search-bar.html",
})
export class SearchBar {
  private readonly router = inject(Router);

  userInput = signal<string>("");
  resultLoading = signal<boolean>(false);
  currentInputValue = "";
  closeSearchbar = output();
  tiles = signal<TileInterface[]>([]);
  showTiles = signal<boolean>(false);
  videos = signal<VideoTileDto[]>([]);
  showVideos = signal<boolean>(false);
  rhContacts = signal<ContactCardInterface[]>([]);
  showRhContacts = signal<boolean>(false);
  mobilityContacts = signal<MobilityContactCardInterface[]>([]);
  showMobilityContacts = signal<boolean>(false);

  searchService = inject(SearchService);
  tilesService = inject(TilesApiService);

  userHasSearched = signal<boolean>(false);

  async onSearch() {
    this.userHasSearched.set(true);
    this.userInput.set(this.currentInputValue.trim());
    try {
      this.resultLoading.set(true);
      const results = await this.searchService.getSearchResults(
        this.userInput()
      );
      this.sortResults(results);
    } finally {
      this.resultLoading.set(false);
    }
  }

  onInputChange(event: any) {
    this.currentInputValue = event.target.value;
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.onSearch();
    } else if (event.key === "Escape") {
      this.closeSearchbar.emit();
    }
  }

  sortResults(results: SearchTileInterface) {
    const sortedTiles: TileInterface[] = [
      ...this.tilesService.mapSearchTilesToTileInterface(
        results.developmentTiles || []
      ),
      ...this.tilesService.mapSearchTilesToTileInterface(
        results.mobilityTiles || []
      ),
      ...this.tilesService.mapSearchTilesToTileInterface(
        results.personalizedJourneyTilesMature || []
      ),
      ...this.tilesService.mapSearchTilesToTileInterface(
        results.personalizedJourneyTilesUnMature || []
      ),
    ];

    this.tiles.set(sortedTiles);
    this.videos.set(results.videoTiles || []);
    this.rhContacts.set(results.contactsRH || []);
    this.mobilityContacts.set(results.contactsMobility || []);
  }

  asStringArray(value: string[] | string | null | undefined): string[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string" && value.trim().length > 0) {
      return [value];
    }

    return [];
  }

  onVideoClick(video: VideoTileDto) {
    if (video.link && video.isExternalLink) {
      window.open(video.link, "_blank", "noopener,noreferrer");
    }

    this.closeSearchbar.emit();
  }

  onTileClick(tile: TileInterface) {
    if (tile.ctaLink) {
      if (tile.isExternalLink) {
        window.open(tile.ctaLink, "_blank", "noopener,noreferrer");
      } else {
        this.router.navigate([tile.ctaLink]);
      }
    }

    this.closeSearchbar.emit();
  }
}
